/**
 * Route configuration for the application
 * Centralizes all route definitions with their associated components and guards
 */

import { lazy } from 'react';
import type { UserRole } from '../types';
import { Environment } from '../utils/environment';

// Lazy load feature components for code splitting
const LoginPage = lazy(() => import('@/features/auth'));
const UnderDevelopment = lazy(() => import('@/features/shared/underDevelopment'));
const ClassSummary = lazy(() => import('@/features/shared/classSummary/components/ClassSummary'));
const Profile = lazy(() => import('@/features/shared/profile'));

// Student features
const StudentDashboard = lazy(() => import('@/features/student/studentDashboard'));
const KRSManagement = lazy(() => import('@/features/student/krsManagement'));
const ClassSchedule = lazy(() => import('@/features/student/studentSchedule'));
const GradesKHS = lazy(() => import('@/features/student/gradesKHS'));
const StudentAttendance = lazy(() => import('@/features/student/studentAttendance'));
const Transcript = lazy(() => import('@/features/student/transcript'));
const MBKMActivities = lazy(() => import('@/features/student/mbkmActivities'));
const StudentSettings = lazy(() => import('@/features/student/studentSettings'));

// Lecturer features
const LecturerDashboard = lazy(() => import('@/features/lecturer/lecturerDashboard'));
const LecturerClassSchedule = lazy(() => import('@/features/lecturer/lecturerSchedule'));
const GradeInput = lazy(() => import('@/features/lecturer/gradeInput'));
const StudentPerformance = lazy(() => import('@/features/lecturer/studentPerformance'));
const AcademicAdvisor = lazy(() => import('@/features/lecturer/academicAdvisor'));
const LecturerSettings = lazy(() => import('@/features/lecturer/lecturerSettings'));

// Admin features
const AdminDashboard = lazy(() => import('@/features/admin/adminDashboard'));
const ClassManagement = lazy(() => import('@/features/admin/classManagement'));
const MasterData = lazy(() => import('@/features/admin/masterData'));
const Programs = lazy(() => import('@/features/admin/programs'));
const Curriculum = lazy(() => import('@/features/admin/curriculum'));
const UserManagement = lazy(() => import('@/features/admin/userManagement'));
const RoleRequestManagement = lazy(() => import('@/features/admin/roleRequests'));
const SemesterSetup = lazy(() => import('@/features/admin/semesterSetup'));
const ClassScheduling = lazy(() => import('@/features/admin/classScheduling'));
const Reports = lazy(() => import('@/features/admin/adminReports'));
const AttendanceSummary = lazy(() => import('@/features/admin/attendanceSummary'));
const AdminSettings = lazy(() => import('@/features/admin/adminSettings'));

// Guest features
const GuestDashboard = lazy(() => import('@/features/guest/guestDashboard'));
const RoleManagement = lazy(() => import('@/features/guest/roleManagement'));
const GuestProfile = lazy(() => import('@/features/guest/guestDashboard').then(m => ({ default: m.GuestProfile })));

// Other role features
const StaffDashboard = lazy(() => import('@/features/other/staffDashboard'));
const ApplicantDashboard = lazy(() => import('@/features/other/applicantDashboard'));
const AlumniDashboard = lazy(() => import('@/features/other/alumniDashboard'));

/**
 * Route configuration interface
 */
export interface RouteConfig {
  path: string;
  component: React.LazyExoticComponent<React.FC>;
  isEnabled: boolean;
  requiresAuth: boolean;
  roles?: UserRole[];
}

/**
 * Public routes (no authentication required)
 */
export const publicRoutes: RouteConfig[] = [
  {
    path: '/login',
    component: LoginPage,
    isEnabled: true,
    requiresAuth: false,
  },
];

/**
 * Shared routes (accessible by multiple authenticated roles)
 */
export const sharedRoutes: RouteConfig[] = [
  {
    path: '/under-development',
    component: UnderDevelopment,
    isEnabled: true,
    requiresAuth: true,
  },
  {
    path: '/class-summary/:classId',
    component: ClassSummary,
    isEnabled: true,
    requiresAuth: true,
  },
];

/**
 * Student routes
 */
export const studentRoutes: RouteConfig[] = [
  {
    path: '/student/dashboard',
    component: StudentDashboard,
    isEnabled: Environment.pages.studentDashboard,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/krs',
    component: KRSManagement,
    isEnabled: Environment.pages.studentKRS,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/schedule',
    component: ClassSchedule,
    isEnabled: Environment.pages.studentSchedule,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/grades',
    component: GradesKHS,
    isEnabled: Environment.pages.studentGrades,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/attendance',
    component: StudentAttendance,
    isEnabled: Environment.pages.studentAttendance,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/transcript',
    component: Transcript,
    isEnabled: Environment.pages.studentTranscript,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/activities',
    component: MBKMActivities,
    isEnabled: Environment.pages.studentActivities,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/profile',
    component: Profile,
    isEnabled: Environment.pages.studentProfile,
    requiresAuth: true,
    roles: ['student'],
  },
  {
    path: '/student/settings',
    component: StudentSettings,
    isEnabled: Environment.pages.studentSettings,
    requiresAuth: true,
    roles: ['student'],
  },
];

/**
 * Lecturer routes
 */
export const lecturerRoutes: RouteConfig[] = [
  {
    path: '/lecturer/dashboard',
    component: LecturerDashboard,
    isEnabled: Environment.pages.lecturerDashboard,
    requiresAuth: true,
    roles: ['lecturer'],
  },
  {
    path: '/lecturer/schedule',
    component: LecturerClassSchedule,
    isEnabled: Environment.pages.lecturerSchedule,
    requiresAuth: true,
    roles: ['lecturer'],
  },
  {
    path: '/lecturer/grade-input',
    component: GradeInput,
    isEnabled: Environment.pages.lecturerGradeInput,
    requiresAuth: true,
    roles: ['lecturer'],
  },
  {
    path: '/lecturer/performance',
    component: StudentPerformance,
    isEnabled: Environment.pages.lecturerPerformance,
    requiresAuth: true,
    roles: ['lecturer'],
  },
  {
    path: '/lecturer/advisor',
    component: AcademicAdvisor,
    isEnabled: Environment.pages.lecturerAdvisor,
    requiresAuth: true,
    roles: ['lecturer'],
  },
  {
    path: '/lecturer/profile',
    component: Profile,
    isEnabled: Environment.pages.lecturerProfile,
    requiresAuth: true,
    roles: ['lecturer'],
  },
  {
    path: '/lecturer/settings',
    component: LecturerSettings,
    isEnabled: Environment.pages.lecturerSettings,
    requiresAuth: true,
    roles: ['lecturer'],
  },
];

/**
 * Admin routes
 */
export const adminRoutes: RouteConfig[] = [
  {
    path: '/admin/dashboard',
    component: AdminDashboard,
    isEnabled: Environment.pages.adminDashboard,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/classes',
    component: ClassManagement,
    isEnabled: Environment.pages.adminClasses,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/master-data',
    component: MasterData,
    isEnabled: Environment.pages.adminMasterData,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/programs',
    component: Programs,
    isEnabled: Environment.pages.adminPrograms,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/curriculum',
    component: Curriculum,
    isEnabled: Environment.pages.adminCurriculum,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/users',
    component: UserManagement,
    isEnabled: Environment.pages.adminUsers,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/role-requests',
    component: RoleRequestManagement,
    isEnabled: Environment.pages.adminRoleRequests,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/semester',
    component: SemesterSetup,
    isEnabled: Environment.pages.adminSemester,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/scheduling',
    component: ClassScheduling,
    isEnabled: Environment.pages.adminScheduling,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/reports',
    component: Reports,
    isEnabled: Environment.pages.adminReports,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/attendance-summary',
    component: AttendanceSummary,
    isEnabled: Environment.pages.adminAttendanceSummary,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/profile',
    component: Profile,
    isEnabled: Environment.pages.adminProfile,
    requiresAuth: true,
    roles: ['admin'],
  },
  {
    path: '/admin/settings',
    component: AdminSettings,
    isEnabled: Environment.pages.adminSettings,
    requiresAuth: true,
    roles: ['admin'],
  },
];

/**
 * Guest routes
 */
export const guestRoutes: RouteConfig[] = [
  {
    path: '/guest/dashboard',
    component: GuestDashboard,
    isEnabled: Environment.pages.guestDashboard,
    requiresAuth: true,
    roles: ['guest'],
  },
  {
    path: '/guest/role-management',
    component: RoleManagement,
    isEnabled: Environment.pages.guestRoleManagement,
    requiresAuth: true,
    roles: ['guest'],
  },
  {
    path: '/guest/profile',
    component: GuestProfile,
    isEnabled: Environment.pages.guestProfile,
    requiresAuth: true,
    roles: ['guest'],
  },
];

/**
 * Other role dashboard routes
 */
export const otherRoleRoutes: RouteConfig[] = [
  {
    path: '/staff/dashboard',
    component: StaffDashboard,
    isEnabled: Environment.pages.staffDashboard,
    requiresAuth: true,
    roles: ['staff'],
  },
  {
    path: '/student_applicant/dashboard',
    component: ApplicantDashboard,
    isEnabled: Environment.pages.applicantDashboard,
    requiresAuth: true,
    roles: ['student_applicant'],
  },
  {
    path: '/alumni/dashboard',
    component: AlumniDashboard,
    isEnabled: Environment.pages.alumniDashboard,
    requiresAuth: true,
    roles: ['alumni'],
  },
];

/**
 * All protected routes combined
 */
export const allProtectedRoutes: RouteConfig[] = [
  ...sharedRoutes,
  ...studentRoutes,
  ...lecturerRoutes,
  ...adminRoutes,
  ...guestRoutes,
  ...otherRoleRoutes,
];

/**
 * Redirect configurations for short URLs
 */
export const redirectRoutes = [
  { from: '/student', to: '/student/dashboard' },
  { from: '/lecturer', to: '/lecturer/dashboard' },
  { from: '/admin', to: '/admin/dashboard' },
  { from: '/guest', to: '/guest/dashboard' },
  { from: '/', to: '/dashboard' },
] as const;
